// Shared formatter for note / call / task body text coming from the server.
//
// The backend stores these as either:
//   - pre-formatted HTML (campaign-generated content)
//   - plain text with `-` bullets and `\n` line breaks (sales-sequence AI)
//   - one long sentence with inline section headings like "Opening:",
//     "Value Prop:" etc. (older AI scripts)
//
// We normalise everything into HTML so downstream renderers (parse() in
// ListingTaskCard / ListingNoteCard) can drop it into JSX with paragraph
// spacing and bullet structure intact.
//
// Per the no-em-dashes rule and the user's "no font-weight on headings"
// request, headings are emitted as plain `<p>` tags rather than wrapped
// in `<strong>`.

import { decompressString } from "../../../../utils/helpers";

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Decompress safely (the backend uses pako + base64 + URI encoding for some
// payloads, raw pako for others). Returns the original value on failure.
export const safeDecompress = (val) => {
  if (val == null) return val;
  try {
    return decodeURIComponent(decompressString(val));
  } catch (e) {
    try {
      return decompressString(val);
    } catch (e2) {
      return val;
    }
  }
};

// AI scripts sometimes pack section headings inline with no newline before
// them. Inject a paragraph break so the formatter can lay them out cleanly.
const SECTION_HEADINGS = [
  "Opening",
  "Value Prop",
  "Discovery Questions",
  "Handling Objections",
  "Closing",
  "Objective",
  "Talking Points",
  "Next Steps",
  "Goal",
  "Personalization hook",
  "Position the value",
  "Soft close for meeting",
  "If not the right person",
  "Log outcomes",
];

const splitInlineHeadings = (text) => {
  const escaped = SECTION_HEADINGS.map((h) =>
    h.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  );
  const pattern = new RegExp(
    `(?<=\\S)\\s+(?=\\b(?:${escaped.join("|")})\\s*:)`,
    "g",
  );
  return text.replace(pattern, "\n\n");
};

// Convert plain text into structured HTML.
//   - Bullet lines (`- foo`, `* foo`, `• foo`) become <ul><li>
//   - Blank lines mark paragraph boundaries
//   - Lines that match "Heading:" patterns become their own <p>
//     (no <strong>, just regular text per user preference)
//   - Inline-only "- foo - bar - baz" runs also split on " - " so they
//     don't collapse into a single paragraph
export const formatPlainTextToHtml = (input) => {
  if (typeof input !== "string") return input;
  // If it already looks like HTML, leave it alone.
  if (/<[a-zA-Z][^>]*>/.test(input)) return input;

  const rawText = input.replace(/\r\n/g, "\n").trim();
  if (!rawText) return "";

  // Pre-process: explode inline headings, then explode inline " - " bullets
  // when the line has no actual newlines (this is the "one wall of text"
  // case visible in the notes screenshot).
  let text = splitInlineHeadings(rawText);
  if (!/\n/.test(text)) {
    // Single line with inline " - " separators: turn each "- foo" run into
    // its own line so the bullet logic below picks them up.
    text = text.replace(/\s+-\s+(?=\S)/g, "\n- ");
  }

  const blocks = text.split(/\n{2,}/);

  const renderBlock = (block) => {
    const lines = block
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) return "";

    const isBullet = (l) => /^[•\-*]\s+/.test(l);

    if (lines.every(isBullet)) {
      const items = lines
        .map(
          (l) =>
            `<li>${escapeHtml(l.replace(/^[•\-*]\s+/, ""))}</li>`,
        )
        .join("");
      return `<ul>${items}</ul>`;
    }

    const parts = [];
    let bulletBuffer = [];
    const flushBullets = () => {
      if (bulletBuffer.length) {
        parts.push(
          `<ul>${bulletBuffer
            .map((b) => `<li>${escapeHtml(b)}</li>`)
            .join("")}</ul>`,
        );
        bulletBuffer = [];
      }
    };

    lines.forEach((l) => {
      if (isBullet(l)) {
        bulletBuffer.push(l.replace(/^[•\-*]\s+/, ""));
        return;
      }
      flushBullets();
      // Headings get rendered as plain paragraphs (no <strong>) so they
      // inherit the surrounding text weight.
      parts.push(`<p>${escapeHtml(l)}</p>`);
    });
    flushBullets();
    return parts.join("");
  };

  return blocks.map(renderBlock).join("");
};

// One-shot helper: decompress (if needed), then format. Returns HTML safe
// to drop into a `parse()` call.
export const decompressAndFormat = (raw, isCompressed) => {
  if (raw == null) return "";
  const decoded = isCompressed ? safeDecompress(raw) : raw;
  return formatPlainTextToHtml(decoded);
};
